import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Search, X, Check, MessageSquare, DoorOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useLobbySocket } from "../hooks/useLobbySocket";
import QuizSection from "../components/QuizSection";
import { PlayerCard } from "../components/PlayerCard";
import { CareerPathBoard } from "../components/CareerPathBoard";
import Avatar from "../components/Avatar";
import { UserName } from "../components/UserName";
import { countryFlag, fmtMs, DIFFICULTY_COLORS, tweenNumber } from "../lib/derive";
import { quizCategoryDisplay } from "../lib/quiz_categories";
import { toast } from "sonner";

function TimerRing({ totalSec, remainingSec }) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, remainingSec / Math.max(1, totalSec)));
  const dash = c * pct;
  const color = remainingSec <= 5 ? "var(--danger)" : remainingSec < totalSec * 0.5 ? "var(--warning)" : "var(--accent)";
  const pulse = remainingSec <= 5 ? "timer-pulse" : "";
  return (
    <div className={`relative w-24 h-24 ${pulse}`} data-testid="timer-ring">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} stroke="rgba(255,255,255,0.07)" strokeWidth="7" fill="none" />
        <circle
          cx="48" cy="48" r={r}
          stroke={color}
          strokeWidth="7"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          transform="rotate(-90 48 48)"
          style={{ transition: "stroke-dasharray 0.2s linear, stroke 0.3s" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-black tabular-nums" style={{ color }} data-testid="timer-seconds">
          {Math.ceil(Math.max(0, remainingSec))}
        </div>
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">sec</div>
      </div>
    </div>
  );
}

function AnswerInput({ disabled, lockedAnswer, onSubmit, onClear }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [active, setActive] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!q.trim() || disabled || picked) { setResults([]); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/players/search?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data);
        setActive(0);
      } catch {
        setResults([]);
      }
    }, 150);
    return () => timer.current && clearTimeout(timer.current);
  }, [q, disabled, picked]);

  const submit = () => { if (picked) onSubmit(picked); };

  const onKey = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && picked) { e.preventDefault(); submit(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(results.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === "Enter") {
      e.preventDefault();
      const r = results[active];
      if (r) { setPicked(r); setQ(""); setOpen(false); }
    }
    if (e.key === "Escape") setOpen(false);
  };

  if (lockedAnswer) {
    return (
      <motion.div
        initial={{ scale: 0.97, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        data-testid="answer-locked"
        className="rounded-2xl border-2 border-[var(--accent)] bg-[var(--accent)]/10 px-4 py-3.5 flex items-center gap-3"
      >
        <div className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center">
          <Check size={18} strokeWidth={3} className="text-black" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">answer locked</div>
          <div className="font-bold text-[var(--accent)]">{lockedAnswer.name}</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">cannot change</div>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <Search size={16} className="text-zinc-600" />
          </div>
          <input
            data-testid="answer-input"
            type="text"
            disabled={disabled}
            value={picked ? picked.name : q}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onKeyDown={onKey}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setPicked(null); setQ(e.target.value); setOpen(true); }}
            placeholder="Who is the missing teammate?"
            className="w-full pl-11 pr-12 py-3.5 rounded-2xl bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none disabled:opacity-40 text-base"
          />
          {picked && (
            <button
              type="button"
              onClick={() => { setPicked(null); setQ(""); onClear && onClear(); }}
              data-testid="answer-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Clear answer"
            >
              <X size={16} />
            </button>
          )}
          <AnimatePresence>
            {open && !picked && results.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="absolute z-20 mt-2 w-full max-h-72 overflow-auto rounded-2xl border border-white/10 bg-[var(--surface)] shadow-2xl"
              >
                {results.map((r, i) => (
                  <button
                    key={r._id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => { setPicked(r); setQ(""); setOpen(false); }}
                    data-testid={`answer-suggestion-${r._id}`}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition ${
                      i === active ? "bg-[var(--accent)]/10" : "hover:bg-white/5"
                    }`}
                  >
                    {r.image_url ? (
                      <img src={r.image_url} alt="" className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                    ) : (
                      <span className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-bold">
                        {(r.name || "?")[0]}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{r.name}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                        {r.position || ""} {r.nationality ? `· ${r.nationality}` : ""}
                      </div>
                    </div>
                    <span className="text-lg">{countryFlag(r.nationality)}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <button
          onClick={submit}
          disabled={disabled || !picked}
          data-testid="answer-submit"
          className="px-6 py-3.5 rounded-2xl bg-[var(--accent)] text-black font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-110 transition"
        >
          Submit
        </button>
      </div>
    </div>
  );
}

function LeaderboardRow({ row, rank, currentUserId, isLeader, locked, playersById }) {
  const [display, setDisplay] = useState(row.total);
  const prev = useRef(row.total);
  useEffect(() => {
    if (row.total !== prev.current) {
      tweenNumber(prev.current, row.total, 700, setDisplay);
      prev.current = row.total;
    }
  }, [row.total]);
  const fullUser = (playersById && playersById[row.user_id]) || row;
  return (
    <motion.li
      layout
      initial={{ opacity: 0, x: 10 }}
      animate={locked ? { opacity: 1, x: 0, scale: [1, 1.03, 1] } : { opacity: 1, x: 0 }}
      transition={locked ? { scale: { duration: 0.45 } } : undefined}
      data-testid={`leaderboard-row-${row.user_id}`}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition ${
        row.user_id === currentUserId
          ? "bg-[var(--accent)]/10 border border-[var(--accent)]/40"
          : "bg-black/30 border border-white/5"
      } ${locked ? "ring-1 ring-emerald-400/50" : ""}`}
    >
      <span className="text-zinc-500 w-5 text-right font-mono text-xs">{rank}</span>
      {isLeader && <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] animate-pulse" />}
      <UserName user={fullUser} className="flex-1 font-semibold truncate" />
      {locked && (
        <span data-testid={`leaderboard-locked-${row.user_id}`}
          className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-emerald-400 text-black animate-pulse">
          LOCKED IN
        </span>
      )}
      <span className="font-mono font-bold tabular-nums">{display}</span>
    </motion.li>
  );
}

function ScoringBadge({ label, value }) {
  if (!value) return null;
  return (
    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--accent)]/15 text-[var(--accent)] font-bold">
      {label} +{value}
    </span>
  );
}

function RevealOverlay({ reveal, correctCard, currentUserId }) {
  const firedRef = useRef(false);
  useEffect(() => {
    if (!reveal) { firedRef.current = false; return; }
    const fc = (reveal.scoreboard || []).find((s) => s.first_correct);
    if (fc && !firedRef.current) {
      firedRef.current = true;
      // Confetti burst — origin slightly right where leaderboard sits on desktop
      confetti({
        particleCount: 70,
        spread: 75,
        origin: { x: 0.85, y: 0.45 },
        colors: ["#00FF87", "#ffffff", "#1abf6b"],
        scalar: 0.9,
      });
      if (fc.user_id === currentUserId) {
        confetti({
          particleCount: 120,
          spread: 90,
          origin: { x: 0.5, y: 0.6 },
          colors: ["#00FF87", "#ffffff"],
        });
      }
    }
  }, [reveal, currentUserId]);

  if (!reveal) return null;
  const cp = reveal.correct_player;
  const quizReveal = reveal.quiz_reveal;
  // Quiz mode: show the question + correct option(s) instead of the player card.
  if (quizReveal) {
    return (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="reveal-overlay"
        className="fixed inset-0 z-30 bg-black/80 backdrop-blur-md flex items-center justify-center px-6 py-8"
      >
        <motion.div initial={{ scale: 0.9, y: 12 }} animate={{ scale: 1, y: 0 }} className="max-w-2xl w-full">
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">
              round {reveal.round_number} · reveal
            </div>
            <h2 className="text-2xl sm:text-3xl font-black mt-3">
              {quizReveal.question_text}
            </h2>
            <div className="mt-4 inline-block px-5 py-3 rounded-2xl bg-[var(--accent)]/12 border border-[var(--accent)]/40">
              <div className="text-[10px] uppercase tracking-widest text-[var(--accent)]">correct answer</div>
              <div className="text-xl font-black text-[var(--accent)] mt-0.5" data-testid="reveal-correct-text">
                {quizReveal.correct_answer_text || "—"}
              </div>
            </div>
          </div>
          {Array.isArray(quizReveal.options) && quizReveal.options.length > 0 && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {quizReveal.options.map((o, i) => (
                <div
                  key={i}
                  data-testid={`reveal-option-${i}`}
                  className={`px-4 py-3 rounded-xl border text-sm ${
                    o.is_correct
                      ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)] font-bold"
                      : "border-white/10 bg-white/[0.03] text-zinc-400"
                  }`}
                >
                  <span className="text-[10px] uppercase tracking-widest mr-2">{String.fromCharCode(65 + i)}</span>
                  {o.text}
                </div>
              ))}
            </div>
          )}
          <ul className="mt-5 space-y-1.5 text-sm">
            {(reveal.scoreboard || []).map((s) => (
              <li key={s.user_id} className={`flex items-center gap-3 px-3 py-2 rounded-xl ${
                s.user_id === currentUserId ? "bg-[var(--accent)]/10 border border-[var(--accent)]/30" : "bg-white/5 border border-white/5"
              }`}>
                <span className="w-6 text-right text-zinc-500">{s.is_correct ? "✓" : "·"}</span>
                <UserName user={s} className="flex-1 font-semibold truncate" />
                <span className="text-[10px] text-zinc-500 tabular-nums">{fmtMs(s.response_time_ms)}</span>
                <span className="font-mono font-bold tabular-nums w-12 text-right">+{s.points_this_round}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 text-center text-[11px] uppercase tracking-widest text-zinc-500">
            next round in a few seconds
          </div>
        </motion.div>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="reveal-overlay"
      className="fixed inset-0 z-30 bg-black/80 backdrop-blur-md flex items-center justify-center px-6 py-8"
    >
      <motion.div
        initial={{ scale: 0.9, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">
            round {reveal.round_number} · reveal
          </div>
          <h2 className="text-3xl sm:text-4xl font-black mt-2">
            Correct: <span className="text-[var(--accent)]">{cp?.name}</span>
          </h2>
        </div>

        <div className="mt-6 grid sm:grid-cols-[260px_1fr] gap-6 items-center">
          <div className="mx-auto sm:mx-0 w-full max-w-[260px]">
            <PlayerCard
              player={cp}
              team={correctCard?.team || null}
              startYear={correctCard?.start_year}
              endYear={correctCard?.end_year}
              variant="correct"
              testId="reveal-card"
            />
          </div>
          <ul className="space-y-1.5 text-sm">
            {(reveal.scoreboard || []).map((s) => (
              <li key={s.user_id} className={`flex items-center gap-3 px-3 py-2 rounded-xl ${
                s.user_id === currentUserId ? "bg-[var(--accent)]/10 border border-[var(--accent)]/30" : "bg-white/5 border border-white/5"
              }`}>
                <span className="w-6 text-right text-zinc-500">{s.is_correct ? "✓" : "·"}</span>
                <UserName user={s} className="flex-1 font-semibold truncate" />
                <span className="text-[10px] text-zinc-500 tabular-nums">{fmtMs(s.response_time_ms)}</span>
                <span className="font-mono font-bold tabular-nums w-12 text-right">
                  +{s.points_this_round}
                </span>
                {s.scoring_breakdown && (
                  <span className="flex flex-wrap gap-1 ml-1">
                    {s.scoring_breakdown.base > 0 && <ScoringBadge label="base" value={s.scoring_breakdown.base} />}
                    {s.scoring_breakdown.speed > 0 && <ScoringBadge label="speed" value={s.scoring_breakdown.speed} />}
                    {s.first_correct && <ScoringBadge label="1st" value={s.scoring_breakdown.first} />}
                    {s.scoring_breakdown.streak > 0 && <ScoringBadge label="streak" value={s.scoring_breakdown.streak} />}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-5 text-center text-[11px] uppercase tracking-widest text-zinc-500">
          next round in a few seconds
        </div>
      </motion.div>
    </motion.div>
  );
}

function CategoryPickerOverlay({ picker, currentUserId, totalSec, remainingSec, onPick }) {
  const isPicker = picker.picker_user_id === currentUserId;
  const cats = useMemo(() => picker.categories || [], [picker.categories]);
  const picked = picker.picked || null;
  const disabled = !!picked || remainingSec <= 0;

  // Keyboard: 1/2/3/4 to pick (picker only).
  useEffect(() => {
    if (!isPicker || disabled) return;
    const onKey = (e) => {
      const n = Number(e.key);
      if (!Number.isFinite(n) || n < 1 || n > cats.length) return;
      const c = cats[n - 1];
      if (c) onPick(c.key);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPicker, disabled, cats, onPick]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="category-picker-overlay"
      className="fixed inset-0 z-30 bg-black/80 backdrop-blur-md flex items-center justify-center px-6 py-8"
    >
      <motion.div
        initial={{ scale: 0.95, y: 12 }}
        animate={{ scale: 1, y: 0 }}
        className="max-w-2xl w-full"
      >
        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent)]">
            round {picker.round_number} / {picker.total_rounds} · category pick
          </div>
          <h2 className="text-2xl sm:text-3xl font-black mt-2" data-testid="category-picker-title">
            {isPicker ? (
              <>Your turn — pick a category</>
            ) : (
              <>
                <span className="text-[var(--accent)]">{picker.picker_username || "Player"}</span>{" "}
                is picking…
              </>
            )}
          </h2>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <span className="w-2 h-2 rounded-full bg-[var(--accent)] animate-pulse" />
            <span className="text-[11px] uppercase tracking-widest text-zinc-300 tabular-nums">
              auto-pick in {Math.ceil(Math.max(0, remainingSec))}s
            </span>
          </div>
        </div>

        {isPicker && cats.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="category-picker-options">
            {cats.map((c, i) => {
              const meta = quizCategoryDisplay(c.key);
              const isPicked = picked === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  data-testid={`category-pick-${c.key}`}
                  disabled={disabled}
                  onClick={() => onPick(c.key)}
                  className={`group relative text-left rounded-2xl border-2 px-5 py-5 transition flex items-center gap-4
                    ${isPicked
                      ? "border-[var(--accent)] bg-[var(--accent)]/15 ring-2 ring-[var(--accent)]/60"
                      : "border-white/10 bg-[var(--surface)] hover:border-[var(--accent)]/60 hover:bg-[var(--accent)]/8"}
                    ${disabled && !isPicked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className="w-12 h-12 shrink-0 rounded-xl flex items-center justify-center text-2xl"
                    style={{ background: `${meta.accent}22`, border: `1px solid ${meta.accent}55` }}
                    aria-hidden
                  >
                    {c.icon || meta.icon}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[9px] uppercase tracking-[0.3em] text-zinc-500">
                      option {i + 1}
                    </span>
                    <span className="block text-lg font-black mt-0.5 truncate">
                      {c.display_name || meta.name}
                    </span>
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-widest text-zinc-500 px-1.5 py-0.5 rounded border border-white/10 font-mono"
                    aria-hidden
                  >
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          <div
            data-testid="category-picker-waiting"
            className="rounded-2xl border border-white/10 bg-[var(--surface)] px-6 py-10 text-center"
          >
            <div className="text-5xl mb-3" aria-hidden>🎲</div>
            <div className="text-base text-zinc-300">
              Waiting for{" "}
              <span className="font-bold text-[var(--accent)]">{picker.picker_username || "Player"}</span>{" "}
              to pick a category…
            </div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mt-2">
              if they go AFK, a random category is locked in automatically.
            </div>
          </div>
        )}

        <div className="mt-5 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          {isPicker ? "press 1 – 4 to pick · choice is final" : "next round category will appear once picked"}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function LobbyGame() {
  const { code } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [round, setRound] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [reveal, setReveal] = useState(null);
  const [revealCard, setRevealCard] = useState(null);
  const [lockedAnswer, setLockedAnswer] = useState(null);
  const [lockedUserIds, setLockedUserIds] = useState(() => new Set());
  const [chat, setChat] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [hints, setHints] = useState([]);  // [{hint_index, label, text, _entered?}]
  const [picker, setPicker] = useState(null); // { round_number, picker_user_id, picker_username, categories, time_limit_sec, server_started_at_ms, picked? }
  const [lobbyMeta, setLobbyMeta] = useState(null); // { host_user_id, is_ranked }
  // Phase-9.1: lookup by user_id so chat rows can render <Avatar> with hydrated favorite_club.
  const [playersById, setPlayersById] = useState({});
  const finishingRef = useRef(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, []);

  const handleEvent = useCallback((evt) => {
    switch (evt.type) {
      case "lobby_state": {
        const ar = evt.payload?.active_round;
        // Pre-populate leaderboard from lobby players so it shows from round 1.
        const lobbyPlayers = evt.payload?.players || [];
        const lobby = evt.payload?.lobby || {};
        // Phase-9: stash lobby meta so the header door button knows host/ranked.
        setLobbyMeta({
          host_user_id: lobby.host_user_id,
          is_ranked: !!lobby.is_ranked,
          lobby_code: lobby.lobby_code,
        });
        if (lobbyPlayers.length) {
          setPlayersById(Object.fromEntries(lobbyPlayers.map((p) => [p.user_id, p])));
          setLeaderboard((prev) => prev.length ? prev : lobbyPlayers
            .slice()
            .sort((a, b) => (a.username || "").localeCompare(b.username || ""))
            .map((p) => ({
              user_id: p.user_id, username: p.username, avatar_url: p.avatar_url,
              total: 0, is_moderator: !!p.is_moderator,
            })));
        }
        if (ar) {
          setRound(ar);
          setServerOffsetMs(ar.server_started_at_ms - Date.now());
          if (ar.my_answer) setLockedAnswer({ _id: ar.my_answer.guessed_player_id, name: "you've answered" });
          // Phase-5 reconnect: surface any hints that already fired (no animation).
          if (Array.isArray(ar.hints_revealed_so_far)) {
            setHints(ar.hints_revealed_so_far.map((h) => ({ ...h, _entered: false })));
          }
        }
        // Phase-9.2: Quiz picker replay on reconnect — if the active_picker
        // snapshot is present and no round is active yet, rebuild picker UI so
        // the user doesn't stall on the "connecting" screen.
        const ap = evt.payload?.active_picker;
        if (ap && !ar) {
          setServerOffsetMs((ap.server_started_at_ms || Date.now()) - Date.now());
          setPicker((prev) => {
            if (prev && prev.round_number === ap.round_number && prev.picker_user_id === ap.picker_user_id) {
              return { ...prev, ...ap, categories: ap.categories || prev.categories };
            }
            return { ...ap, picked: null };
          });
        }
        if (evt.payload?.status === "finished") navigate(`/lobby/${code}/results`, { replace: true });
        break;
      }
      case "round_started":
        setRound(evt.payload);
        setServerOffsetMs(evt.payload.server_started_at_ms - Date.now());
        setLockedAnswer(null);
        setLockedUserIds(new Set());
        setReveal(null);
        setHints([]);
        setPicker(null);
        // Keep existing totals — leaderboard rows just lose their "locked" chip.
        break;
      case "category_picker_started": {
        const p = evt.payload || {};
        setLockedAnswer(null);
        setReveal(null);
        setRevealCard(null);
        // Sync clock from server.
        setServerOffsetMs(p.server_started_at_ms - Date.now());
        // If we receive the "watching" payload (categories=null) AFTER the
        // personal one, merge instead of clobbering the categories list.
        setPicker((prev) => {
          if (prev && prev.round_number === p.round_number && prev.picker_user_id === p.picker_user_id) {
            return {
              ...prev,
              ...p,
              categories: p.categories || prev.categories,
            };
          }
          return { ...p, picked: null };
        });
        break;
      }
      case "answer_submitted": {
        const uid = evt.payload?.user_id;
        if (uid) setLockedUserIds((prev) => {
          if (prev.has(uid)) return prev;
          const next = new Set(prev);
          next.add(uid);
          return next;
        });
        break;
      }
      case "hint_revealed": {
        const h = evt.payload || {};
        setHints((prev) => {
          if (prev.some((x) => x.hint_index === h.hint_index)) return prev;
          return [...prev, { ...h, _entered: true }];
        });
        break;
      }
      case "round_ended": {
        // try to find the card whose displayed_player matches correct_player (used for visual reveal)
        const matching = (evt.payload?.correct_player && round?.cards) || [];
        const matchedCard = matching.length ? round.cards.find((c) => c.context_team?._id === matching[0]?.team_id) : null;
        setRevealCard(matchedCard ? { team: matchedCard.context_team, start_year: matchedCard.context_start_year, end_year: matchedCard.context_end_year } : null);
        setReveal(evt.payload);
        setLeaderboard(evt.payload.scoreboard || []);
        setTimeout(() => setReveal(null), 4800);
        break;
      }
      case "leaderboard_updated":
        setLeaderboard(evt.payload.leaderboard || []);
        break;
      case "game_finished":
        if (!finishingRef.current) {
          finishingRef.current = true;
          try { sessionStorage.setItem(`footguesser_results_${code}`, JSON.stringify(evt.payload)); } catch { /* ignore */ }
          setTimeout(() => navigate(`/lobby/${code}/results`, { replace: true }), 2200);
        }
        break;
      case "chat_message":
        setChat((prev) => [...prev.slice(-99), evt.payload]);
        break;
      case "achievement_unlocked": {
        const a = evt.payload || {};
        toast.success(
          `${a.name || "Achievement unlocked"}`,
          {
            description: a.description || "",
            duration: 6000,
            id: `ach-${a.slug}`,
            className: "achievement-toast",
          }
        );
        break;
      }
      case "error":
        if (evt.payload?.message) toast.error(evt.payload.message);
        break;
      case "lobby_disbanded": {
        const host = evt.payload?.host_username || "Host";
        toast.error(`Game was terminated by ${host}`);
        navigate("/dashboard", { replace: true });
        break;
      }
      default: break;
    }
  }, [code, navigate, round]);

  const { status: wsStatus, send } = useLobbySocket(code, handleEvent);

  const remainingSec = useMemo(() => {
    if (!round) return 0;
    const endsAtClient = round.server_ends_at_ms - serverOffsetMs;
    return Math.max(0, (endsAtClient - now) / 1000);
  }, [round, now, serverOffsetMs]);

  const pickerRemainingSec = useMemo(() => {
    if (!picker) return 0;
    const endsAtClient =
      picker.server_started_at_ms + (picker.time_limit_sec || 10) * 1000 - serverOffsetMs;
    return Math.max(0, (endsAtClient - now) / 1000);
  }, [picker, now, serverOffsetMs]);

  const onAnswer = (player) => {
    if (lockedAnswer) return;
    setLockedAnswer(player);
    const ok = send("submit_answer", { guessed_player_id: player._id });
    if (!ok) { toast.error("Not connected — try again"); setLockedAnswer(null); }
  };

  /** Quiz answer handler — receives { kind, text, optionIndex?, id? } from QuizSection. */
  const onQuizAnswer = (sub) => {
    if (lockedAnswer) return;
    setLockedAnswer({ name: sub.text || "answered", optionIndex: sub.optionIndex, ...sub });
    const payload = {};
    if (typeof sub.optionIndex === "number") payload.submitted_option_index = sub.optionIndex;
    if (sub.text) payload.submitted_text = sub.text;
    if (sub.id) payload.submitted_id = sub.id;
    const ok = send("submit_answer", payload);
    if (!ok) { toast.error("Not connected — try again"); setLockedAnswer(null); }
  };

  const onChatSend = (text) => send("chat_message", { text });

  const onPickCategory = (categoryKey) => {
    if (!picker) return;
    if (picker.picker_user_id !== user?.id) return;
    if (picker.picked) return;
    // Optimistically mark picked so the button shows the selected state.
    setPicker((prev) => (prev ? { ...prev, picked: categoryKey } : prev));
    const ok = send("pick_category", { category_key: categoryKey });
    if (!ok) {
      toast.error("Not connected — try again");
      setPicker((prev) => (prev ? { ...prev, picked: null } : prev));
    }
  };

  const isHost = lobbyMeta?.host_user_id && lobbyMeta.host_user_id === user?.id;
  const canTerminate = isHost && !lobbyMeta?.is_ranked;

  const onExitGame = async () => {
    if (canTerminate) {
      // eslint-disable-next-line no-alert
      if (!window.confirm("Terminating ends the game for everyone in progress. Continue?")) return;
      try {
        await api.post(`/lobbies/${code}/disband`);
        toast.success("Game terminated");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not terminate");
      }
    } else {
      // eslint-disable-next-line no-alert
      if (!window.confirm("Leaving forfeits your current game. Continue?")) return;
      try {
        await api.post(`/lobbies/${code}/leave`);
        navigate("/dashboard", { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not leave");
      }
    }
  };

  if (!round && !picker) {
    return (
      <div data-testid="lobby-game-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center">
        <div className="text-center">
          <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] mb-2">connecting</div>
          <div className="text-lg">Joining the game…</div>
          <div className="text-[10px] text-zinc-600 mt-2">ws: {wsStatus}</div>
        </div>
      </div>
    );
  }

  if (!round && picker) {
    return (
      <div data-testid="lobby-game-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative">
        <header className="px-4 sm:px-6 py-4 max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to={`/lobby/${code}`} className="flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-zinc-400 hover:text-[var(--accent)] transition">
            ← <span className="font-mono">{code}</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Round <span className="text-[var(--text)] font-bold">{picker.round_number}</span>/{picker.total_rounds}
            </span>
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
              quiz
            </span>
          </div>
          <div className="w-24" />
        </header>
        <AnimatePresence>
          <CategoryPickerOverlay
            key={`picker-r${picker.round_number}`}
            picker={picker}
            currentUserId={user?.id}
            totalSec={picker.time_limit_sec || 10}
            remainingSec={pickerRemainingSec}
            onPick={onPickCategory}
          />
        </AnimatePresence>
      </div>
    );
  }

  const leaderId = leaderboard[0]?.user_id;

  return (
    <div data-testid="lobby-game-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="px-4 sm:px-6 py-4 max-w-6xl mx-auto flex items-center justify-between gap-4">
        <Link to={`/lobby/${code}`} className="flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-zinc-400 hover:text-[var(--accent)] transition">
          ← <span className="font-mono">{code}</span>
        </Link>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Round <span className="text-[var(--text)] font-bold">{round.round_number}</span>/{round.total_rounds}
          </span>
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
            {round.mode}
          </span>
          {round.mode !== "quiz" && round.difficulty && (
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${DIFFICULTY_COLORS[round.difficulty] || ""}`}>
              {round.difficulty}
            </span>
          )}
        </div>
        <TimerRing totalSec={round.time_limit_sec} remainingSec={remainingSec} />
        <button
          type="button"
          onClick={onExitGame}
          data-testid={canTerminate ? "game-terminate" : "game-leave"}
          title={canTerminate ? "Terminate game" : "Leave game"}
          className={`w-9 h-9 rounded-full border flex items-center justify-center transition ${
            canTerminate
              ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
              : "border-white/15 text-zinc-300 hover:bg-white/5"
          }`}
        >
          <DoorOpen size={16} />
        </button>
      </header>

      {round.mode === "quiz" && round.picker_username && round.picked_category_display_name && (
        <div className="px-4 sm:px-6 max-w-6xl mx-auto -mt-1 mb-2">
          <div
            data-testid="round-category-banner"
            className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zinc-400"
          >
            <span className="font-bold text-[var(--text)]">Round {round.round_number}</span>
            <span className="text-zinc-600">·</span>
            <span>
              <span className="text-[var(--accent)] font-bold">{round.picker_username}</span>'s pick
            </span>
            <span className="text-zinc-600">·</span>
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--accent)]/30 bg-[var(--accent)]/10 text-[var(--accent)] tracking-widest"
            >
              <span aria-hidden>
                {quizCategoryDisplay(round.picked_category_key).icon}
              </span>
              <span className="font-bold normal-case tracking-wide">
                {round.picked_category_display_name}
              </span>
            </span>
            {round.picker_auto && (
              <span className="text-[9px] text-amber-400/80 normal-case tracking-wider">
                (auto-picked · AFK)
              </span>
            )}
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 pb-24 grid lg:grid-cols-[1fr_300px] gap-6">
        <section className="space-y-5">
          {round.mode === "quiz" ? (
            <QuizSection
              key={`quiz-r${round.round_number}`}
              round={round}
              locked={lockedAnswer}
              onSubmit={onQuizAnswer}
              remainingSec={remainingSec}
            />
          ) : round.mode === "career_path" ? (
            <div data-testid="game-career-path">
              <CareerPathBoard career={round.career_path || []} />
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4" data-testid="game-cards">
              {round.cards?.map((c, i) => (
                <PlayerCard
                  key={i}
                  player={c.displayed_player}
                  team={c.context_team}
                  startYear={c.context_start_year}
                  endYear={c.context_end_year}
                  index={i}
                />
              ))}
            </div>
          )}
          <AnimatePresence>
            {hints.length > 0 && (
              <motion.div
                key="hint-row"
                data-testid="hint-pills"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-wrap gap-2 items-center"
              >
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 pr-1">
                  hints
                </span>
                {hints.map((h) => (
                  <motion.span
                    key={h.hint_index}
                    initial={h._entered ? { opacity: 0, y: -6 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    data-testid={`hint-pill-${h.hint_index}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--surface)] border border-white/10 pl-2 pr-3 py-1"
                  >
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent)] bg-[var(--accent)]/10 px-2 py-0.5 rounded-full border border-[var(--accent)]/30">
                      {h.label}
                    </span>
                    <span className="text-xs text-zinc-100 font-medium tracking-wide">
                      {h.text}
                    </span>
                  </motion.span>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
          {round.mode !== "quiz" && (
            <AnswerInput
              key={`ans-r${round?.round_number ?? 0}`}
              disabled={remainingSec <= 0}
              lockedAnswer={lockedAnswer}
              onSubmit={onAnswer}
            />
          )}
          <div className="text-[11px] text-zinc-500 text-center">
            {round.mode === "quiz"
              ? "Press enter to lock in your answer."
              : "Find the player who shared a dressing room with all three."}
          </div>
        </section>

        <aside className="hidden lg:flex flex-col gap-4">
          <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">leaderboard</div>
            <ul className="space-y-1.5">
              {leaderboard.map((r, i) => (
                <LeaderboardRow key={r.user_id} row={r} rank={i + 1} currentUserId={user?.id} isLeader={r.user_id === leaderId} locked={lockedUserIds.has(r.user_id)} playersById={playersById} />
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-3 flex flex-col" style={{ maxHeight: 280 }}>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-1 mb-1.5">chat</div>
            <div className="flex-1 overflow-y-auto text-xs space-y-1.5 px-1" data-testid="game-chat-list">
              {chat.length === 0 && <div className="text-zinc-600 italic">silent…</div>}
              {chat.map((m, i) => {
                const sender = playersById[m.user_id] || { username: m.username, user_id: m.user_id, is_donator: m.is_donator, is_moderator: m.is_moderator };
                return (
                  <div key={i} className="flex items-start gap-1.5">
                    <Avatar user={sender} size="sm" />
                    <div className="min-w-0">
                      <UserName user={sender} hideModBadge className={`font-semibold ${m.user_id === user?.id ? "text-[var(--accent)]" : "text-zinc-300"}`} />
                      <span className="text-zinc-400">: {m.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = e.currentTarget.elements.namedItem("msg").value.trim();
                if (v) { onChatSend(v); e.currentTarget.reset(); }
              }}
              className="mt-2"
            >
              <input
                name="msg"
                maxLength={200}
                placeholder="message…"
                data-testid="game-chat-input"
                className="w-full text-xs px-2.5 py-2 rounded-lg bg-black border border-white/10 focus:border-[var(--accent)] outline-none"
              />
            </form>
          </div>
        </aside>
      </main>

      {/* Mobile chat drawer trigger */}
      <button
        onClick={() => setChatOpen((v) => !v)}
        data-testid="mobile-chat-toggle"
        className="lg:hidden fixed bottom-5 right-5 z-20 w-12 h-12 rounded-full bg-[var(--accent)] text-black flex items-center justify-center shadow-[0_10px_30px_-5px_var(--accent)]"
        aria-label="Open chat"
      >
        <MessageSquare size={20} />
      </button>
      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            className="lg:hidden fixed inset-x-0 bottom-0 z-30 bg-[var(--surface)] border-t border-white/10 rounded-t-2xl p-4 max-h-[60vh] flex flex-col"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-[var(--accent)]">Chat & Leaderboard</div>
              <button onClick={() => setChatOpen(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <ul className="space-y-1.5 mb-3">
              {leaderboard.map((r, i) => (
                <LeaderboardRow key={r.user_id} row={r} rank={i + 1} currentUserId={user?.id} isLeader={r.user_id === leaderId} locked={lockedUserIds.has(r.user_id)} playersById={playersById} />
              ))}
            </ul>
            <div className="flex-1 overflow-y-auto text-xs space-y-1.5">
              {chat.map((m, i) => {
                const sender = playersById[m.user_id] || { username: m.username, user_id: m.user_id, is_donator: m.is_donator, is_moderator: m.is_moderator };
                return (
                  <div key={i} className="flex items-start gap-1.5">
                    <Avatar user={sender} size="sm" />
                    <div className="min-w-0">
                      <UserName user={sender} hideModBadge className={`font-semibold ${m.user_id === user?.id ? "text-[var(--accent)]" : "text-zinc-300"}`} />
                      <span className="text-zinc-400">: {m.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = e.currentTarget.elements.namedItem("msg").value.trim();
                if (v) { onChatSend(v); e.currentTarget.reset(); }
              }}
              className="mt-2"
            >
              <input
                name="msg"
                maxLength={200}
                placeholder="message…"
                className="w-full text-sm px-3 py-2 rounded-lg bg-black border border-white/10 focus:border-[var(--accent)] outline-none"
              />
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {reveal && <RevealOverlay reveal={reveal} correctCard={revealCard} currentUserId={user?.id} />}
      </AnimatePresence>

      <AnimatePresence>
        {picker && (
          <CategoryPickerOverlay
            key={`picker-r${picker.round_number}`}
            picker={picker}
            currentUserId={user?.id}
            totalSec={picker.time_limit_sec || 10}
            remainingSec={pickerRemainingSec}
            onPick={onPickCategory}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
